import type { ComponentProps } from 'react';
import { cn } from '@/lib/cn';

/**
 * The Overseer mark: a closed O with one hand at twelve.
 *
 * From the brand design's chosen direction — "O'clock, in turquoise". It is a
 * clock and a letter at once, which is the whole idea for a timesheet system.
 *
 * Two things about the geometry are load-bearing rather than aesthetic. The
 * 15-unit stroke on a 100-unit viewBox is heavy on purpose: the mark has to
 * survive a 16px favicon, and a thinner ring greys out and disappears at that
 * size. And the hand is a `rx="6.5"` rounded rect that stops short of the
 * centre — it reads as a hand at twelve rather than as a dot or a full radius.
 *
 * Colour comes from `currentColor`, so one component serves every colourway the
 * design specifies: brand teal in the header, white reversed on a brand fill,
 * `--fg` in a single-colour context. Set it with a text utility on the caller —
 * `text-brand` for the mark's own colour. Colour lives in tokens, never here.
 *
 * `aria-hidden` by default because the wordmark beside it already says
 * "Overseer"; a screen reader announcing it twice is noise. Pass an
 * `aria-label` and `aria-hidden={false}` where the mark stands alone.
 */
export function Logo({ className, ...props }: ComponentProps<'svg'>) {
  return (
    <svg
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      focusable="false"
      className={cn('shrink-0', className)}
      {...props}
    >
      <circle cx="50" cy="50" r="34" fill="none" stroke="currentColor" strokeWidth="15" />
      <rect x="43.5" y="24" width="13" height="28" rx="6.5" fill="currentColor" />
    </svg>
  );
}
